import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PaymentsService } from '../../../services/payment.service';
import { ClientCreditMovement } from '../../../shared/interfaces';
import { ToastService } from '../../../shared/services';
import { parseDbUtc } from '../../../shared/common/utils';

type AdjustMode = 'add' | 'deduct';

/**
 * Client's "saldo a favor": current balance, quick add/deduct and the full movement history.
 */
@Component({
  selector: 'app-client-credit',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './client-credit.html',
  styleUrls: ['../people-profile/people-shared.css', './client-credit.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientCredit implements OnChanges {
  @Input() clientId: string | null = null;
  @Input() clientName = '';
  /** Emits the new balance after every successful change. */
  @Output() balanceChanged = new EventEmitter<number>();

  @ViewChild('adjustDialog') adjustDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('historyDialog') historyDialog?: ElementRef<HTMLDialogElement>;

  private paymentsService = inject(PaymentsService);
  private toastService = inject(ToastService);

  private static readonly HISTORY_PAGE_SIZE = 20;
  readonly quickAmounts = [20000, 50000, 100000];

  isLoading = signal(false);
  loadFailed = signal(false);
  balance = signal(0);
  totalCredited = signal(0);
  totalUsed = signal(0);
  totalCount = signal(0);
  recent = signal<ClientCreditMovement[]>([]);

  // Adjust dialog
  mode = signal<AdjustMode>('add');
  amount = signal(0);
  reason = signal('');
  isSaving = signal(false);
  submitted = signal(false);

  // History dialog
  history = signal<ClientCreditMovement[]>([]);
  historyPage = signal(1);
  historyLoading = signal(false);
  hasMoreHistory = computed(() => this.history().length < this.totalCount());

  newBalance = computed(() =>
    this.mode() === 'add' ? this.balance() + this.amount() : this.balance() - this.amount()
  );
  amountError = computed<string | null>(() => {
    if (this.amount() <= 0) return 'Escribe un monto mayor a cero.';
    if (this.mode() === 'deduct' && this.amount() > Math.max(this.balance(), 0)) {
      return `Solo puedes descontar hasta ${this.formatMoney(Math.max(this.balance(), 0))}.`;
    }
    return null;
  });
  reasonError = computed<string | null>(() =>
    this.mode() === 'deduct' && !this.reason().trim() ? 'Indica por qué descuentas el saldo.' : null
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && this.clientId) {
      this.load();
    }
  }

  load(): void {
    if (!this.clientId) return;
    this.isLoading.set(true);
    this.loadFailed.set(false);
    this.paymentsService.getClientCreditMovements(this.clientId, 1, 3).subscribe({
      next: (res) => {
        this.balance.set(res.balance);
        this.totalCredited.set(res.totalCredited);
        this.totalUsed.set(res.totalUsed);
        this.totalCount.set(res.totalCount);
        this.recent.set(res.items);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.loadFailed.set(true);
      },
    });
  }

  openAdjust(mode: AdjustMode): void {
    this.mode.set(mode);
    this.amount.set(0);
    this.reason.set('');
    this.submitted.set(false);
    this.adjustDialog?.nativeElement.showModal();
    setTimeout(() => this.adjustDialog?.nativeElement.querySelector<HTMLInputElement>('#cc-amount')?.focus());
  }

  closeAdjust(): void {
    this.adjustDialog?.nativeElement.close();
  }

  onAmountInput(value: string): void {
    const digits = value.replace(/\D/g, '');
    this.amount.set(digits ? Math.min(parseInt(digits, 10), 1_000_000_000) : 0);
  }

  submitAdjust(): void {
    this.submitted.set(true);
    if (!this.clientId || this.amountError() || this.reasonError() || this.isSaving()) return;

    const amount = this.amount();
    const reason = this.reason().trim();
    const isAdd = this.mode() === 'add';
    const request$ = isAdd
      ? this.paymentsService.addClientCredit(this.clientId, { amount, description: reason || undefined })
      : this.paymentsService.deductClientCredit(this.clientId, { amount, reason });

    this.isSaving.set(true);
    request$.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.closeAdjust();
        this.toastService.show(
          isAdd ? `Saldo agregado: ${this.formatMoney(amount)}` : `Saldo descontado: ${this.formatMoney(amount)}`,
          'success'
        );
        this.load();
        this.balanceChanged.emit(this.newBalance());
      },
      error: (err) => {
        this.isSaving.set(false);
        const message = err?.error?.message || err?.error?.errors?.[0]?.errorMessage;
        this.toastService.show(
          message || (isAdd ? 'No pudimos agregar el saldo. Inténtalo de nuevo.' : 'No pudimos descontar el saldo. Inténtalo de nuevo.'),
          'warning'
        );
      },
    });
  }

  openHistory(): void {
    this.history.set([]);
    this.historyPage.set(0);
    this.historyDialog?.nativeElement.showModal();
    this.loadMoreHistory();
  }

  closeHistory(): void {
    this.historyDialog?.nativeElement.close();
  }

  loadMoreHistory(): void {
    if (!this.clientId || this.historyLoading()) return;
    const next = this.historyPage() + 1;
    this.historyLoading.set(true);
    this.paymentsService
      .getClientCreditMovements(this.clientId, next, ClientCredit.HISTORY_PAGE_SIZE)
      .subscribe({
        next: (res) => {
          this.history.update((list) => [...list, ...res.items]);
          this.totalCount.set(res.totalCount);
          this.historyPage.set(next);
          this.historyLoading.set(false);
        },
        error: () => {
          this.historyLoading.set(false);
          this.toastService.show('No pudimos cargar los movimientos.', 'warning');
        },
      });
  }

  /** Closes a native dialog when the click lands on its backdrop. */
  onDialogClick(event: MouseEvent, dialog: HTMLDialogElement): void {
    if (event.target === dialog) dialog.close();
  }

  formatMoney(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(value);
  }

  toDate(value: string): Date {
    return parseDbUtc(value);
  }
}
